/**
 * The waitlist contract, mirrored from the Manasil API.
 *
 * SOURCE OF TRUTH: packages/shared/src/waitlist.ts in the manasil monorepo. This file is a
 * hand-kept copy, which exists only because the site lives in its own repository so Cloud
 * Build can deploy it without monorepo configuration.
 *
 * Keep it deliberately small. The site does NOT validate with these — the server owns the
 * rules and its Zod issues are mapped back onto the fields they came from — so only the
 * shapes and the two constants the form reads are reproduced. No Zod dependency, and
 * nothing here that the form does not actually use.
 *
 * If the waitlist contract changes in the API, it must be changed here too: the failure
 * mode is a signup the server rejects, or a field silently dropped.
 */

/** What someone came for: one of the nine goals, or something we have not thought of. */
export const OTHER_PURPOSE = 'OTHER' as const;

/** Same ceiling as onboarding's goals, plus room for OTHER alongside three real ones. */
export const WAITLIST_MAX_PURPOSES = 4;

/**
 * A goal key (SCREAMING_SNAKE, validated server-side against the goals collection) or the
 * OTHER escape hatch. Left as a string rather than a union of the nine keys: the real list
 * lives in the database, and a narrower type here would lie the moment a tenth is added.
 */
export type WaitlistPurpose = string;

export interface CreateWaitlistBody {
  name: string;
  email: string;
  mobile: string;
  purposes: WaitlistPurpose[];
  /** Free text, only meaningful when OTHER is among the purposes. */
  otherInterest?: string;
  /** Which page or campaign sent them, for reading the funnel later. */
  source?: string;
  /** Honeypot. Real people never see this field, so anything in it is a bot. */
  company?: string;
}

/** The one and only error shape the API ever returns. */
export interface ApiErrorResponse {
  error: {
    code: string;
    message: string;
    details: Record<string, unknown>;
  };
}
